import { forwardRef, useEffect, useRef, useState } from 'react';
import type { PublicUser } from '@orbit-sync/shared-types';
import { useDirectoryStore } from '../chat/directoryStore.js';
import { api, errorMessage } from '../lib/api/index.js';
import { Avatar } from './Avatar.js';
import { EmptyState } from './EmptyState.js';
import { Icon } from './Icon.js';
import { Skeleton } from './Skeleton.js';

/** Long enough that typing a name is one request, short enough to feel live. */
const DEBOUNCE_MS = 250;
const PAGE_SIZE = 30;

interface ListState {
  /** Which query these rows answer, so stale ones are never shown for a new one. */
  query: string;
  items: PublicUser[];
  nextCursor: string | null;
  hasMore: boolean;
  loading: boolean;
  error: string | null;
}

const EMPTY: ListState = {
  query: '',
  items: [],
  nextCursor: null,
  hasMore: true,
  loading: false,
  error: null,
};

export interface PeoplePickerProps {
  /** What the action column says for a row. */
  actionLabel: (user: PublicUser, picked: boolean) => string;
  actionIcon: (picked: boolean) => 'messageSquare' | 'check' | 'plus';
  pickedIds: readonly string[];
  /** People not to offer at all - members already in a group. */
  excludeIds?: readonly string[];
  disabled?: boolean;
  onChoose: (user: PublicUser) => void;
}

/**
 * Everyone you can talk to, A to Z, with a search box that narrows the same
 * list. The first page loads as soon as it opens - so a colleague can be found
 * without knowing how their name is spelt - and more load as the list scrolls.
 *
 * Every request in flight is aborted when the query moves on, so a slow
 * response cannot land late and repaint stale rows.
 */
export const PeoplePicker = forwardRef<HTMLInputElement, PeoplePickerProps>(function PeoplePicker(
  { actionLabel, actionIcon, pickedIds, excludeIds = [], disabled = false, onChoose },
  searchRef,
) {
  const remember = useDirectoryStore((state) => state.remember);
  const [query, setQuery] = useState('');
  const [debounced, setDebounced] = useState('');
  const [list, setList] = useState<ListState>(EMPTY);
  const [page, setPage] = useState(0);
  const sentinelRef = useRef<HTMLLIElement>(null);
  const listRef = useRef(list);

  useEffect(() => {
    listRef.current = list;
  });

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(query.trim()), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  // A new query replaces the list from its first page; each `page` bump after
  // that extends it.
  useEffect(() => {
    const controller = new AbortController();
    const current = listRef.current;
    const fresh = page === 0 || current.query !== debounced;
    const cursor = fresh ? null : current.nextCursor;

    if (!fresh && !current.hasMore) {
      return;
    }

    // Marked through a microtask, not synchronously in the effect body.
    queueMicrotask(() => {
      if (!controller.signal.aborted) {
        setList((state) =>
          fresh
            ? { ...EMPTY, query: debounced, loading: true }
            : { ...state, loading: true, error: null },
        );
      }
    });

    api.users
      .browseDirectory({ q: debounced, cursor, limit: PAGE_SIZE }, controller.signal)
      .then((result) => {
        remember(result.items);
        setList((state) => ({
          query: debounced,
          items: fresh ? result.items : [...state.items, ...result.items],
          nextCursor: result.nextCursor,
          hasMore: result.hasMore,
          loading: false,
          error: null,
        }));
      })
      .catch((cause: unknown) => {
        if (!controller.signal.aborted) {
          setList((state) => ({ ...state, loading: false, error: errorMessage(cause) }));
        }
      });

    return () => controller.abort();
  }, [debounced, page, remember]);

  // Load the next page when the end of the list comes within reach of the
  // scrolling box - on scroll, and after each page lands, so a list shorter
  // than the box keeps filling it. Measured directly rather than with an
  // IntersectionObserver, which browsers pause in hidden or throttled tabs.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const scroller = sentinel?.closest('.modal-scroll');

    if (!sentinel || !scroller) {
      return;
    }

    const check = (): void => {
      const state = listRef.current;

      if (!state.hasMore || state.loading || state.error) {
        return;
      }

      if (sentinel.getBoundingClientRect().top <= scroller.getBoundingClientRect().bottom + 120) {
        setPage((value) => value + 1);
      }
    };

    queueMicrotask(check);
    scroller.addEventListener('scroll', check, { passive: true });
    return () => scroller.removeEventListener('scroll', check);
  }, [list.items.length, list.hasMore, list.loading]);

  const excluded = new Set(excludeIds);
  const rows = list.items.filter((user) => !excluded.has(user.id));
  const settling = query.trim() !== debounced || list.query !== debounced;

  return (
    <>
      <div className="field">
        <label htmlFor="people-search">Find people</label>
        <div className="input-group">
          <Icon name="search" size={15} />
          <input
            ref={searchRef}
            className="input"
            id="people-search"
            name="people-search"
            type="search"
            autoComplete="off"
            value={query}
            placeholder="Filter by name, or type a full email address"
            onChange={(event) => setQuery(event.target.value)}
          />
        </div>
      </div>

      {list.error ? (
        <p className="field-error" role="alert">
          <Icon name="alertCircle" size={13} />
          {list.error}
        </p>
      ) : null}

      <div className="results" aria-busy={list.loading || settling}>
        {rows.length === 0 && (list.loading || settling) ? (
          <div aria-label="Loading people">
            {[0, 1, 2, 3].map((row) => (
              <div className="result-skeleton" key={row}>
                <Skeleton width={28} height={28} circle />
                <Skeleton width={`${55 - row * 8}%`} height={11} />
              </div>
            ))}
          </div>
        ) : rows.length === 0 && !list.hasMore ? (
          <EmptyState
            compact
            icon="search"
            title={debounced ? 'Nobody matches that' : 'Nobody else is here yet'}
            description={
              debounced
                ? 'Check the spelling, or type their complete email address.'
                : 'Colleagues appear here as soon as they have an account.'
            }
          />
        ) : (
          <ul className="result-list result-list--browse">
            {rows.map((user) => {
              const picked = pickedIds.includes(user.id);

              return (
                <li key={user.id}>
                  <button
                    type="button"
                    className={picked ? 'result result--picked' : 'result'}
                    disabled={disabled}
                    aria-pressed={picked}
                    onClick={() => onChoose(user)}
                  >
                    <Avatar name={user.displayName} id={user.id} size="sm" />
                    <span className="result-body">
                      <span className="result-name truncate" dir="auto">
                        {user.displayName}
                      </span>
                    </span>
                    <span className="result-action">
                      <Icon name={actionIcon(picked)} size={13} />
                      {actionLabel(user, picked)}
                    </span>
                  </button>
                </li>
              );
            })}
            {list.hasMore ? (
              <li ref={sentinelRef} className="result-more" aria-hidden="true">
                {list.loading ? <span className="btn-spinner" /> : null}
              </li>
            ) : null}
          </ul>
        )}
      </div>
    </>
  );
});
